import { Injectable } from '@angular/core';
import { AsamcoAuthService, CommonServices } from '@asamco/base';
import { User } from '@asamco/user-management/shared';
import { fixOdataDateOffsetFilter, pathJoin, showErrorSwal, skipTenantInterceptor } from '@asamco/utils';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { from, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import swal from 'sweetalert2';

import { ConnectedApp, ConnectedAppForCompanyDto } from './connected-app.interface';

@Injectable({
	providedIn: 'root',
})
export class ConnectedAppService {
	private readonly baseUrl: string = 'ConnectedApp';

	constructor(private cs: CommonServices, private auth: AsamcoAuthService) {}

	getAllForCompany(companyId: number): Observable<ConnectedAppForCompanyDto[]> {
		return this.cs.http.get<ConnectedAppForCompanyDto[]>(`${this.baseUrl}/forcompany/${companyId}`, {
			headers: skipTenantInterceptor(),
		});
	}

	getAllForCompanies(companyIds: Array<number>): Observable<ConnectedAppForCompanyDto[]> {
		return this.cs.http.post<ConnectedAppForCompanyDto[]>(`${this.baseUrl}/allforcompanies`, companyIds);
	}

	getActiveAllForCompany(companyId: number): Observable<ConnectedAppForCompanyDto[]> {
		return this.cs.http.get<ConnectedAppForCompanyDto[]>(`${this.baseUrl}/activeforcompany/${companyId}`);
	}

	getById(id: number): Observable<ConnectedApp> {
		return this.cs.http.get<ConnectedApp>(`${this.baseUrl}/${id}`);
	}

	getByOid(oid: string): Observable<ConnectedApp> {
		return this.cs.http.get<ConnectedApp>(`${this.baseUrl}/GetByOid/${oid}`);
	}

	update(connectedApp: ConnectedApp) {
		return this.cs.http.put(`${this.baseUrl}/${connectedApp.id}`, connectedApp);
	}

	updateAppUsername(data) {
		return this.cs.http.post('UserConnectedAppAccess/updateappusername', data);
	}

	/**
	 * Saves an Company
	 * @param organisation the saved organisation
	 */
	public save(connectedApp: ConnectedApp) {
		return this.cs.http.post(this.baseUrl, connectedApp);
	}

	/**
	 *
	 * @param connectedAppId
	 */
	public getAuthorizedUsers(connectedAppId: number): Observable<User[]> {
		return this.cs.http.get<User[]>(`userconnectedappaccess/forconnectedapp/${connectedAppId}`, { headers: skipTenantInterceptor() });
	}

	/**
	 * Authorizes the users for a company
	 * @param users
	 * @param companyId
	 */
	public grantAccess(users: { [key: number]: string }, connectedAppId: number) {
		return this.cs.http.post(`${this.baseUrl}/grantaccess`, { users: users, connectedAppId: connectedAppId });
	}

	/**
	 * Revokes access from a user
	 * @param user
	 * @param companyId
	 */
	public revokeAccess(userId: number, connectedAppId: number) {
		return this.cs.http.post(`${this.baseUrl}/revokeaccess`, { userId: userId, itemId: connectedAppId });
	}

	delete(connectedAppId: number) {
		return from(
			swal.fire({
				icon: 'warning',
				title: this.cs.ts.translate('SWAL_TITLE.WARNING'),
				html: this.cs.ts.translate('CONNECTEDAPP.DIALOG.DELETE_QUESTION'),
				showCancelButton: true,
			})
		).pipe(
			switchMap(result => (result.value ? this.cs.http.delete<ConnectedApp>(`${this.baseUrl}/${connectedAppId}`) : from([]))),
			tap(connectedApp => {
				swal.fire({
					icon: 'success',
					title: this.cs.ts.translate('SWAL_TITLE.SUCCESS'),
					html: 'Connected app successfully deleted',
				});
			}),
			catchError(error => {
				showErrorSwal(error);
				return of(error);
			})
		);
	}

	public getCompanyUsersOdataUrl(connectedAppId: number, withAccess: boolean) {
		return pathJoin([
			this.auth.userSession.backend.odataBaseUrl,
			`ConnectedAppUsers/ConnectedApp(ConnectedAppId=${connectedAppId},UsersWithAccess=${withAccess})`,
		]);
	}

	public getConnectedAppUsersOdataDataSource(companyId: number, usersWithAccess: boolean): DataSource {
		return new DataSource({
			paginate: true,
			requireTotalCount: true,
			store: new ODataStore({
				version: 4,
				url: this.getCompanyUsersOdataUrl(companyId, usersWithAccess),
				beforeSend: (request: { params: Object; headers: Object }) => {
					this.auth.dxStoreBeforeSend(request);
				},
				deserializeDates: false,
				onLoading: options => {
					fixOdataDateOffsetFilter(options.filter);
				},
			}),
		});
	}
}
